import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Close, Save, Category, Add, Delete } from '@mui/icons-material';
import { useProductGroup } from '../../context/ProductGroupContext';
import { ProductGroup, ProductGroupCreation, ProductGroupUpdate } from '../../models/ProductGroup';
import { Product } from '../../models/Product';
import { toast } from 'react-toastify';
import ProductGroupSearch from './ProductGroupSearch';

interface ProductGroupFormProps {
  onClose: () => void;
  editGroup?: ProductGroup | null;
}

const ProductGroupForm: React.FC<ProductGroupFormProps> = ({ onClose, editGroup }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [productOrders, setProductOrders] = useState<Record<number, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddProductSection, setShowAddProductSection] = useState(false);
  
  const { createProductGroup, updateProductGroup } = useProductGroup();

  // Initialize the form with existing group data if in edit mode
  useEffect(() => {
    if (editGroup) {
      setName(editGroup.name);
      setDescription(editGroup.description || '');
      
      if (editGroup.products && editGroup.products.length > 0) {
        setSelectedProducts(editGroup.products);
        
        // Create the initial product orders mapping
        const orders: Record<number, number> = {};
        editGroup.products.forEach((product, index) => {
          orders[product.id] = index;
        });
        setProductOrders(orders);
      }
    }
  }, [editGroup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Nazwa grupy jest wymagana');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (editGroup) {
        // Update existing group
        const updateData: ProductGroupUpdate = {
          name,
          description,
          productOrders
        };

        await updateProductGroup(editGroup.id, updateData);
        toast.success('Grupa produktów została zaktualizowana');
      } else {
        // Create new group
        const newGroupData: ProductGroupCreation = {
          name,
          description,
          productIds: selectedProducts.map(p => p.id)
        };

        await createProductGroup(newGroupData);
        toast.success('Nowa grupa produktów została utworzona');
      }
      
      onClose();
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas zapisywania grupy produktów');
      console.error('Failed to save product group:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addProduct = (product: Product) => {
    if (selectedProducts.some(p => p.id === product.id)) {
      return; // Product already selected
    }

    setSelectedProducts([...selectedProducts, product]);
    
    // Assign the next order for this product
    setProductOrders(prev => ({
      ...prev,
      [product.id]: selectedProducts.length
    }));
  };

  const removeProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    
    // Remove from orders and re-adjust other orders
    const newOrders = { ...productOrders };
    delete newOrders[productId];
    
    // Re-adjust orders (optional)
    const remainingProducts = selectedProducts
      .filter(p => p.id !== productId)
      .sort((a, b) => (productOrders[a.id] || 0) - (productOrders[b.id] || 0));
    
    const adjustedOrders: Record<number, number> = {};
    remainingProducts.forEach((product, index) => {
      adjustedOrders[product.id] = index;
    });
    
    setProductOrders(adjustedOrders);
  };

  // Move product up in the list (decrease order)
  const moveProductUp = (productId: number) => {
    const productIndex = selectedProducts.findIndex(p => p.id === productId);
    if (productIndex <= 0) return; // Already at top

    const updatedOrders = { ...productOrders };
    const currentOrder = productOrders[productId] || productIndex;
    const swapWithId = selectedProducts[productIndex - 1].id;
    const swapWithOrder = productOrders[swapWithId] || (productIndex - 1);
    
    updatedOrders[productId] = swapWithOrder;
    updatedOrders[swapWithId] = currentOrder;
    
    setProductOrders(updatedOrders);
    
    // Re-sort the products array for display
    const sortedProducts = [...selectedProducts].sort(
      (a, b) => (updatedOrders[a.id] || 0) - (updatedOrders[b.id] || 0)
    );
    setSelectedProducts(sortedProducts);
  };

  // Move product down in the list (increase order)
  const moveProductDown = (productId: number) => {
    const productIndex = selectedProducts.findIndex(p => p.id === productId);
    if (productIndex === -1 || productIndex >= selectedProducts.length - 1) return; // Already at bottom

    const updatedOrders = { ...productOrders };
    const currentOrder = productOrders[productId] || productIndex;
    const swapWithId = selectedProducts[productIndex + 1].id;
    const swapWithOrder = productOrders[swapWithId] || (productIndex + 1);
    
    updatedOrders[productId] = swapWithOrder;
    updatedOrders[swapWithId] = currentOrder;
    
    setProductOrders(updatedOrders);
    
    // Re-sort the products array for display
    const sortedProducts = [...selectedProducts].sort(
      (a, b) => (updatedOrders[a.id] || 0) - (updatedOrders[b.id] || 0)
    );
    setSelectedProducts(sortedProducts);
  };

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 }
  };

  return (
    <motion.div 
      variants={formVariants}
      initial="hidden"
      animate="visible"
      className="bg-white rounded-lg border border-gray-200 shadow p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <Category className="mr-2 text-primary" />
          {editGroup ? 'Edytuj grupę produktów' : 'Utwórz nową grupę produktów'}
        </h3>
        <motion.button
          onClick={onClose}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Close fontSize="small" />
        </motion.button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-100">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa grupy *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Wprowadź nazwę grupy produktów"
              required
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Opis (opcjonalnie)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              placeholder="Krótki opis grupy produktów..."
            />
          </div>
          
          {/* Products section */}
          <div className="mt-6 overflow-visible">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-800">Produkty w grupie</h4>
              <motion.button
                type="button"
                onClick={() => setShowAddProductSection(!showAddProductSection)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md flex items-center text-sm"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {showAddProductSection ? (
                  <>
                    <Close fontSize="small" className="mr-1" />
                    <span>Zamknij</span>
                  </>
                ) : (
                  <>
                    <Add fontSize="small" className="mr-1" />
                    <span>Dodaj produkty</span>
                  </>
                )}
              </motion.button>
            </div>
            
            {/* Search and add products section */}
            {showAddProductSection && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-4 overflow-visible bg-gray-50 rounded-lg border border-gray-200 p-4"
              >
                <ProductGroupSearch onProductSelect={addProduct} />
              </motion.div>
            )}
            
            {/* Selected products list */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              {selectedProducts.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-500">
                    Nie dodano jeszcze żadnych produktów do tej grupy.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {selectedProducts.map((product, index) => (
                    <li key={product.id} className="p-3 flex items-center justify-between hover:bg-gray-100">
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-500 font-medium text-sm">{index + 1}</span>
                        {product.mobilePrimaryImage ? (
                          <img 
                            src={product.mobilePrimaryImage} 
                            alt={product.name} 
                            className="h-10 w-10 object-contain bg-white rounded border border-gray-100"
                          />
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center bg-gray-100 rounded">
                            <Category fontSize="small" className="text-gray-400" />
                          </div>
                        )}
                        <div className="overflow-hidden">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {product.serialNumber}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <motion.button
                          type="button"
                          onClick={() => moveProductUp(product.id)}
                          disabled={index === 0}
                          className={`p-1 rounded-full ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                          whileHover={index === 0 ? {} : { scale: 1.1 }}
                          whileTap={index === 0 ? {} : { scale: 0.9 }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => moveProductDown(product.id)}
                          disabled={index === selectedProducts.length - 1}
                          className={`p-1 rounded-full ${index === selectedProducts.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'}`}
                          whileHover={index === selectedProducts.length - 1 ? {} : { scale: 1.1 }}
                          whileTap={index === selectedProducts.length - 1 ? {} : { scale: 0.9 }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => removeProduct(product.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-full"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Delete fontSize="small" />
                        </motion.button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        {/* Form actions */}
        <div className="mt-8 flex justify-end space-x-3">
          <motion.button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            disabled={isSubmitting}
          >
            Anuluj
          </motion.button>
          <motion.button
            type="submit"
            className={`px-5 py-2.5 rounded-md flex items-center space-x-1 font-medium ${
              isSubmitting ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-dark'
            }`}
            variants={buttonVariants}
            whileHover={isSubmitting ? {} : "hover"}
            whileTap={isSubmitting ? {} : "tap"}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Zapisywanie...</span>
              </>
            ) : (
              <>
                <Save fontSize="small" />
                <span>{editGroup ? 'Zaktualizuj grupę' : 'Utwórz grupę'}</span>
              </>
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default ProductGroupForm;
